/**
 * OverridePicker — modal that lets a user pick the correct canonical for a
 * misclassified component (§14.1). Triggered from EvidencePanel's "Reject"
 * action.
 *
 * Shows a search-filterable list of: system-default canonicals (vocab_v0),
 * `unknown` as an explicit choice, and any per-org custom canonicals
 * (vocab extensions per §14.3). A "Name something else..." action at the
 * bottom opens the VocabularyExtensionDialog and chains the override after
 * the new canonical lands.
 */
import * as React from 'react';
import { MagnifyingGlass, Plus, X } from '@phosphor-icons/react';
import { useMutation, useQuery } from 'convex/react';
import { Button } from '@fragments-sdk/ui';
import { VOCAB_V0 } from '@fragments-sdk/classifier';
import { api } from '@/convex';
import type { Id } from '@/convex';
import s from './OverridePicker.module.scss';
import { VocabularyExtensionDialog } from './VocabularyExtensionDialog';

interface CanonicalOption {
  id: string;
  label: string;
  origin: 'system' | 'org';
  category?: string;
}

const SYSTEM_OPTIONS: CanonicalOption[] = VOCAB_V0.map((entry) => ({
  id: String(entry.id),
  label: String(entry.id),
  origin: 'system' as const,
  category: entry.category,
}));

export interface OverridePickerProps {
  componentId: Id<'designSystemComponents'>;
  componentName: string;
  orgId: Id<'orgs'>;
  currentCanonical: string;
  onClose: () => void;
  onComplete: (selectedCanonical: string) => void;
}

export function OverridePicker({
  componentId,
  componentName,
  orgId,
  currentCanonical,
  onClose,
  onComplete,
}: OverridePickerProps) {
  const [query, setQuery] = React.useState('');
  const [reason, setReason] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [vocabDialogOpen, setVocabDialogOpen] = React.useState(false);

  const customVocab = useQuery(api.canonicalPrimitiveMap.listCustomVocabulary, {
    orgId,
  });
  const setOverride = useMutation(api.canonicalPrimitiveMap.setOverride);

  const orgOptions: CanonicalOption[] = React.useMemo(
    () =>
      (customVocab ?? [])
        .filter((entry) => entry.active)
        .map((entry) => ({
          id: entry.canonicalId,
          label: entry.name || entry.canonicalId,
          origin: 'org' as const,
          category: entry.category,
        })),
    [customVocab],
  );

  const allOptions = React.useMemo(
    () => [
      { id: 'unknown', label: 'unknown', origin: 'system' as const },
      ...SYSTEM_OPTIONS,
      ...orgOptions,
    ],
    [orgOptions],
  );

  const filtered = React.useMemo(() => {
    const trimmed = query.trim().toLowerCase();
    if (!trimmed) return allOptions;
    return allOptions.filter(
      (o) =>
        o.label.toLowerCase().includes(trimmed) ||
        o.id.toLowerCase().includes(trimmed),
    );
  }, [allOptions, query]);

  React.useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function handlePick(canonical: string) {
    if (canonical === currentCanonical) {
      setError('Pick a different canonical to override');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await setOverride({
        componentId,
        canonical,
        reason: reason.trim() ? reason.trim() : undefined,
      });
      onComplete(canonical);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Override failed');
      setSubmitting(false);
    }
  }

  function handleVocabCreated(canonicalId: string) {
    setVocabDialogOpen(false);
    void handlePick(canonicalId);
  }

  return (
    <>
      <div className={s.overlay} onClick={onClose} role="presentation">
        <div
          className={s.panel}
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-label="Override canonical primitive"
        >
          <header className={s.header}>
            <div>
              <h2 className={s.title}>Pick the right canonical</h2>
              <p className={s.subtitle}>
                Currently classified as{' '}
                <span className={s.currentName}>{currentCanonical}</span>. Pick
                a different canonical for{' '}
                <span className={s.componentName}>{componentName}</span>.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className={s.closeBtn}
              aria-label="Close"
            >
              <X size={14} />
            </button>
          </header>

          <div className={s.searchRow}>
            <MagnifyingGlass size={14} className={s.searchIcon} />
            <input
              type="search"
              autoFocus
              value={query}
              placeholder="Search canonicals..."
              className={s.searchInput}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          <ul className={s.options} role="listbox">
            {filtered.map((option) => (
              <li key={`${option.origin}-${option.id}`}>
                <button
                  type="button"
                  className={s.option}
                  data-current={
                    option.id === currentCanonical ? 'true' : undefined
                  }
                  onClick={() => handlePick(option.id)}
                  disabled={submitting}
                >
                  <span className={s.optionLabel}>{option.label}</span>
                  <span className={s.optionMeta}>
                    {option.origin === 'org'
                      ? 'Custom'
                      : (option.category ?? '')}
                  </span>
                </button>
              </li>
            ))}
            {filtered.length === 0 && (
              <li className={s.empty}>No canonicals match "{query}".</li>
            )}
          </ul>

          <div className={s.reasonRow}>
            <label htmlFor="override-reason" className={s.reasonLabel}>
              Reason (optional)
            </label>
            <input
              id="override-reason"
              type="text"
              className={s.reasonInput}
              placeholder="Why this isn't a {currentCanonical}?"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={submitting}
            />
          </div>

          {error && <div className={s.error}>{error}</div>}

          <footer className={s.footer}>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setVocabDialogOpen(true)}
              disabled={submitting}
            >
              <Plus size={12} /> Name something else
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </Button>
          </footer>
        </div>
      </div>
      {vocabDialogOpen && (
        <VocabularyExtensionDialog
          orgId={orgId}
          onClose={() => setVocabDialogOpen(false)}
          onCreated={handleVocabCreated}
        />
      )}
    </>
  );
}
